import { useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import {
    GetMapMarkersDocument,
    MapScope,
    type GetMapMarkersQuery,
    type GetMapMarkersQueryVariables,
} from '@/graphql/types';

// Load markers in chunks so a single request never returns everything at once.
const PAGE_SIZE = 2500;

export type MapMarker = NonNullable<
    NonNullable<GetMapMarkersQuery['mapMarkers']>['markers'][number]
>;

interface UseMapMarkersQueryArgs {
    scope: MapScope;
    useDeviceGps?: boolean;
}

export function useMapMarkersQuery({ scope, useDeviceGps = false }: UseMapMarkersQueryArgs) {
    const client = useApolloClient();
    const [markers, setMarkers] = useState<MapMarker[]>([]);
    const [loading, setLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(true);
    const [error, setError] = useState<Error | undefined>();

    useEffect(() => {
        let cancelled = false;
        const archived = scope === MapScope.User ? false : undefined;
        const collected: MapMarker[] = [];

        setMarkers([]);
        setLoading(true);
        setIsLoadingMore(true);
        setError(undefined);

        // Fetch one chunk at a time, until we have all the markers.
        async function loadPage(offset: number): Promise<void> {
            const { data } = await client.query<GetMapMarkersQuery, GetMapMarkersQueryVariables>({
                query: GetMapMarkersDocument,
                variables: {
                    scope,
                    archived,
                    limit: PAGE_SIZE,
                    offset,
                    useDeviceGps,
                },
                fetchPolicy: 'no-cache',
            });
            if (cancelled) {
                return;
            }

            const page = data.mapMarkers;
            const pageMarkers = (page?.markers ?? []).filter(
                (marker): marker is MapMarker => marker != null
            );
            collected.push(...pageMarkers);

            setMarkers([...collected]);
            setLoading(false);

            if (page?.hasMore) {
                await loadPage(offset + PAGE_SIZE);
            }
        }

        void loadPage(0)
            .catch((caughtError: Error) => {
                if (!cancelled) {
                    setError(caughtError);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                    setIsLoadingMore(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [client, scope, useDeviceGps]);

    return {
        loading,
        isLoadingMore,
        error,
        markers,
    };
}
